<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import usePlaylistStore from '../../stores/playlist';
import { useDisplay } from 'vuetify';
import useNavigationStore from '../../stores/navigation';
import useNavigationTriggerStore from '../../stores/navigation-trigger';
import type { GoToItem } from '../../types';
import useLocale from '../../composables/useLocale';

type Emits = {
  (e: 'goToContent', item: GoToItem): void;
};
const emit = defineEmits<Emits>();

const { xs } = useDisplay();
const { totalPlaylistItems, isInitializing, resolutionProgress } = storeToRefs(usePlaylistStore());
const { setScrubbing } = useNavigationStore();
const { setNavigationTrigger } = useNavigationTriggerStore();
const { currentPageIndex } = storeToRefs(useNavigationStore());
const { t } = useLocale();

const liveSliderValue = ref(0);

const sliderMax = computed(() => {
  // Always use the playlist (audio tracks) count, not the page list as the track count !== page count
  return Math.max(0, totalPlaylistItems.value - 1);
});

const currentSliderValue = computed(() => {
  return currentPageIndex.value;
});

watch(
  currentSliderValue,
  (newValue) => {
    liveSliderValue.value = newValue;
  },
  { immediate: true },
);

const handleUpdate = (val: number) => {
  liveSliderValue.value = val;
  setScrubbing(val);
};

const handleChange = (newValue: number) => {
  setScrubbing(null);
  setNavigationTrigger('SLIDER');

  const currentIdx = currentSliderValue.value;
  emit('goToContent', {
    target: newValue,
    isGoingForwardsInBook: newValue > currentIdx,
  });
};

onMounted(() => {
  liveSliderValue.value = currentSliderValue.value;
});
</script>

<template>
  <div class="progress-wrapper d-flex flex-column justify-center w-100" data-testid="bp-playlist-progress-slider">
    <!-- Slider row with prepend/append inline -->
    <div class="d-flex align-center justify-space-between w-100" :class="{ 'mt-2': xs }">
      <!-- Prepend Slot -->
      <div class="d-flex align-center">
        <slot name="prepend"></slot>
      </div>

      <!-- Main Progress / Slider -->
      <div class="d-flex align-center flex-grow-1 px-2">
        <v-locale-provider class="w-100">
          <v-progress-linear v-if="isInitializing" :model-value="resolutionProgress" height="6" rounded class="mx-6" />
          <v-slider
            v-else
            :model-value="liveSliderValue"
            class="audio-progress-slider w-100"
            :min="0"
            :max="sliderMax"
            hide-details
            step="1"
            color="brand-original"
            :aria-label="t('book_player.label_audio_progress_slider')"
            track-size="6"
            thumb-size="24"
            :max-width="440"
            @end="handleChange"
            @update:model-value="handleUpdate"
          />
        </v-locale-provider>
      </div>

      <!-- Append Slot -->
      <div class="d-flex align-center">
        <slot name="append" />
      </div>
    </div>

    <!-- Bottom Slot row (with offset to align with slider) -->
    <div class="bottom-slot-wrapper">
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.progress-wrapper {
  min-height: 48px;
}

.bottom-slot-wrapper {
  width: 100%;
  padding-left: calc(40px + 0.5rem); // Approximate prepend width + padding
  padding-right: calc(40px + 0.5rem); // Approximate append width + padding
}

:deep(.v-slider) {
  cursor: grab;
}

:deep(.v-slider-thumb__label) {
  top: -20px !important;
  background-color: transparent !important;
  color: rgb(var(--v-theme-on-brand)) !important;
}

:deep(.v-slider-thumb__surface) {
  z-index: 100 !important;
  position: relative !important;
}

:deep(.v-slider-thumb__label::before) {
  color: transparent;
}

:deep(.v-slider__thumb-container .v-slider__thumb-label) {
  height: 12px !important;
  background-color: transparent !important;
  border-color: transparent !important;

  .thumb-label-text {
    font-weight: bold;
    padding-top: 12px;
  }
}
// Override from node_modules/vuetify/src/components/VSlider/variables.sass
$slider-thumb-focused-size-increase: 30px;
$slider-thumb-size: 20px;

:deep(.v-slider__thumb) {
  width: $slider-thumb-size;
  height: $slider-thumb-size;
  left: calc($slider-thumb-focused-size-increase / 2);

  &:before {
    width: #{$slider-thumb-size + $slider-thumb-focused-size-increase};
    height: #{$slider-thumb-size + $slider-thumb-focused-size-increase};
    left: calc($slider-thumb-focused-size-increase / 2);
    top: calc($slider-thumb-focused-size-increase / 2);
  }
}

:deep(.v-slider--disabled .v-slider__thumb) {
  width: #{$slider-thumb-size - 4};
  height: #{$slider-thumb-size - 4};
  left: calc(#{$slider-thumb-size - 4} / 2);
}
</style>
